import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import * as FinanceEndpoints from '../api/api-endpoints';
import { RestApiService } from '../api/rest-api.service';
import { formatString } from '../utils/string.utils';
import { SearchBarQueryPayload } from '../../shared/search-filter-pagination/search-bar/search-bar.types';
import { ConfigurationSettingsService } from './configuration-settings.service';
import { CurrencyService } from './currency.service';

export type FinanceTaxScope = 'sales' | 'purchase' | 'both';
export type FinanceTaxAmountType = 'percentage' | 'fixed' | 'division' | 'python_code' | 'group';

export function normalizeFinanceTaxScope(...values: unknown[]): FinanceTaxScope | '' {
  for (const value of values) {
    if (typeof value !== 'string') continue;
    const normalized = value.trim().toLowerCase().replace(/[_\s-]+/g, '');
    if (normalized === 'purchase' || normalized === 'purchases' || normalized === 'buy') {
      return 'purchase';
    }
    if (normalized === 'sales' || normalized === 'sale' || normalized === 'sell') {
      return 'sales';
    }
    if (normalized === 'both' || normalized === 'none' || normalized === 'salesandpurchases') {
      return 'both';
    }
  }
  return '';
}

export interface FinanceTaxRecord {
  id: string;
  name: string;
  tax_type: string;
  scope: FinanceTaxScope;
  amount_type: FinanceTaxAmountType;
  amount: number;
  include_in_price: boolean;
  included_in_price: string | null;
  active: boolean;
  sequence: number;
  account_mapping: string | null;
  description: string | null;
  internal_note: string | null;
  usage_count: number;
  can_delete: boolean;
  created_at: string;
  updated_at: string;
}

export interface FinanceTaxPayload {
  name: string;
  tax_type: string;
  scope: FinanceTaxScope;
  amount_type: FinanceTaxAmountType;
  amount: number;
  include_in_price: boolean;
  active: boolean;
  sequence: number;
  account_mapping?: string | null;
  description?: string | null;
}

export interface FinanceTaxCalculationRequest {
  base_amount: number;
  fiscal_position_id?: string | null;
  quantity?: number;
  tax_ids?: string[];
  currency?: string | null;
  scope?: FinanceTaxScope;
  use_default_tax?: boolean;
}

export interface FinanceTaxCalculationLine {
  tax_id: string | null;
  name: string;
  amount_type: FinanceTaxAmountType;
  base_amount: number;
  tax_amount: number;
  included_in_price: boolean;
  sequence: number;
}

export interface FinanceTaxCalculationResult {
  currency: string | null;
  base_amount: number;
  subtotal: number;
  untaxed_amount: number;
  included_tax_amount: number;
  excluded_tax_amount: number;
  tax_amount: number;
  total: number;
  tax_rate: number;
  tax_ids: string[];
  default_tax_ids: string[];
  lines: FinanceTaxCalculationLine[];
  warnings: string[];
}

export interface FinanceCurrencyRecord {
  id: string;
  code: string;
  name: string;
  symbol: string | null;
  company_base_currency: boolean;
  active: boolean;
  active_for_purchases?: boolean;
  selectable_on_documents?: boolean;
}

export interface FinanceAccountOption {
  value: string;
  label: string;
  code?: string | null;
}

export interface FinanceCustomerRecord {
  id: string;
  account_name: string;
  contact_name: string;
  email: string | null;
  phone: string | null;
  receivables_balance: number;
  invoice_count: number;
  payment_count: number;
  follow_up_status: string;
}

export interface FinancePaymentTermRecord {
  id: string;
  name: string;
  due_days: number;
  installment_rule: string | null;
  description: string | null;
  is_default: boolean;
  active: boolean;
  usage_count: number;
  can_delete: boolean;
  created_at: string;
  updated_at: string;
}

export interface FinanceFollowUpLevelRecord {
  id: string;
  name: string;
  delay_days: number;
  reminder_action: string | null;
  message_template: string | null;
  sequence: number;
  active: boolean;
  usage_count: number;
  can_delete: boolean;
  created_at: string;
  updated_at: string;
}

export interface FinanceIncotermRecord {
  id: string;
  code: string;
  name: string;
  description: string | null;
  active: boolean;
  usage_count: number;
  can_delete: boolean;
  created_at: string;
  updated_at: string;
}

export interface FinancePaymentTermPayload {
  name: string;
  due_days: number;
  installment_rule?: string | null;
  description?: string | null;
  is_default?: boolean;
  active?: boolean;
}

export interface FinanceFollowUpLevelPayload {
  name: string;
  delay_days: number;
  reminder_action?: string | null;
  message_template?: string | null;
  sequence?: number;
  active?: boolean;
}

export interface FinanceIncotermPayload {
  code: string;
  name: string;
  description?: string | null;
  active?: boolean;
}

export interface FinanceBankAccountRecord {
  id: string;
  bank_name: string;
  account_name: string;
  account_number: string;
  currency: string;
  journal_mapping: string | null;
  active: boolean;
  usage_count: number;
  can_delete: boolean;
  created_at: string;
  updated_at: string;
}

export interface FinanceBankAccountPayload {
  bank_name: string;
  account_name: string;
  account_number: string;
  currency: string;
  journal_mapping?: string | null;
  active?: boolean;
}

export interface FinanceReconciliationModelRecord {
  id: string;
  label: string;
  description: string | null;
  matching_rules: string | null;
  amount_matching: string;
  partner_matching: string | null;
  account_mapping: string | null;
  sequence: number;
  active: boolean;
  usage_count: number;
  can_delete: boolean;
  created_at: string;
  updated_at: string;
}

export interface FinanceReconciliationModelPayload {
  label: string;
  description?: string | null;
  matching_rules?: string | null;
  amount_matching?: string | null;
  partner_matching?: string | null;
  account_mapping?: string | null;
  sequence?: number;
  active?: boolean;
}

export interface FinanceOperationListQuery {
  recurringOnly?: boolean;
  originId?: string;
  page?: number;
  perPage?: number;
  search?: string | null;
  state?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  sortBy?: string | null;
  sortDirection?: 'asc' | 'desc' | null;
  searchPayload?: SearchBarQueryPayload | null;
  projectId?: string | null;
}

export interface FinanceOperationPageResponse {
  page: string;
  title: string;
  records: Array<Record<string, any>>;
  summary: Record<string, any>;
  pagination: {
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
}

export interface FinanceOperationReportResponse {
  report: string;
  title: string;
  summary: Record<string, any>;
  lines: Array<Record<string, any>>;
  options?: Record<string, any>;
  warnings?: string[];
  features?: Record<string, any>;
  generated_at?: string;
  data_source?: string;
}

@Injectable({
  providedIn: 'root',
})
export class FinanceService {
  private readonly bankAccountsEndpoint = '/finance/bank-accounts';
  private readonly reconciliationModelsEndpoint = '/finance/reconciliation-models';
  private readonly defaultSalesTaxSettingKey = 'finance.product.default_sales_tax';
  private readonly defaultPurchaseTaxSettingKey = 'finance.product.default_purchase_tax';
  private readonly taxRequests = new Map<string, Promise<FinanceTaxRecord[]>>();
  private readonly accountOptionRequests = new Map<string, Promise<FinanceAccountOption[]>>();

  constructor(
    private readonly restApi: RestApiService,
    private readonly configurationSettings: ConfigurationSettingsService,
    private readonly currencyService: CurrencyService,
  ) {}

  async getInvoiceFormData() {
    return await firstValueFrom(this.restApi.get(FinanceEndpoints.FINANCE_GET_INVOICE_FORM_ENDPOINT));
  }

  async getBillFormData() {
    return await firstValueFrom(this.restApi.get(FinanceEndpoints.FINANCE_GET_BILL_FORM_ENDPOINT));
  }

  async getSettings() {
    const response: any = await firstValueFrom(this.configurationSettings.all());
    const values = response?.values && typeof response.values === 'object' ? response.values : {};

    return {
      ...values,
      default_sales_tax: this.settingText(
        values[this.defaultSalesTaxSettingKey] ?? values['default_sales_tax']
      ),
      default_purchase_tax: this.settingText(
        values[this.defaultPurchaseTaxSettingKey] ?? values['default_purchase_tax']
      ),
    };
  }

  async getInvoices() {
    return await firstValueFrom(this.restApi.get(FinanceEndpoints.FINANCE_GET_INVOICES_ENDPOINT));
  }

  createInvoice(payload: any) {
    return this.restApi.post(FinanceEndpoints.FINANCE_POST_INVOICES_ENDPOINT, payload);
  }

  createBill(payload: any) {
    return this.restApi.post(FinanceEndpoints.FINANCE_POST_BILLS_ENDPOINT, payload);
  }

  updateSettings(payload: any) {
    const values: Record<string, unknown> = {
      [this.defaultSalesTaxSettingKey]: this.settingText(payload?.default_sales_tax) || null,
      [this.defaultPurchaseTaxSettingKey]: this.settingText(payload?.default_purchase_tax) || null,
    };

    return this.configurationSettings.savePageValues('finance-settings', values);
  }

  searchInvoices(payload: any) {
    return this.restApi.post(FinanceEndpoints.FINANCE_POST_INVOICES_SEARCH_ENDPOINT, payload);
  }

  async getTaxes(filters?: { include_inactive?: boolean }): Promise<FinanceTaxRecord[]> {
    const cacheKey = JSON.stringify({
      include_inactive: filters?.include_inactive === true,
    });
    const cached = this.taxRequests.get(cacheKey);
    if (cached) {
      return (await cached).map((tax) => ({ ...tax }));
    }

    const request = this.fetchAllTaxes(filters).catch((error) => {
      this.taxRequests.delete(cacheKey);
      throw error;
    });

    this.taxRequests.set(cacheKey, request);
    return (await request).map((tax) => ({ ...tax }));
  }

  async getChartOfAccountOptions(query = ''): Promise<FinanceAccountOption[]> {
    const normalized = query.trim().toLowerCase();
    const cacheKey = normalized || '__all__';
    const cached = this.accountOptionRequests.get(cacheKey);
    if (cached) return (await cached).map((option) => ({ ...option }));

    const params = new URLSearchParams({ limit: '100' });
    if (normalized) params.set('query', normalized);
    const request = firstValueFrom(
      this.restApi.get<{ options?: FinanceAccountOption[] }>(
        `${FinanceEndpoints.FINANCE_GET_CHART_OF_ACCOUNTS_OPTIONS_ENDPOINT}?${params.toString()}`,
      ),
    ).then((response) => response.options ?? []).catch((error) => {
      this.accountOptionRequests.delete(cacheKey);
      throw error;
    });
    this.accountOptionRequests.set(cacheKey, request);
    return (await request).map((option) => ({ ...option }));
  }

  private async fetchAllTaxes(filters?: { include_inactive?: boolean }): Promise<FinanceTaxRecord[]> {
    const firstPage = await this.fetchTaxPage(1, filters);
    const records = [...firstPage.records];

    for (let page = 2; page <= firstPage.totalPages; page += 1) {
      const nextPage = await this.fetchTaxPage(page, filters);
      records.push(...nextPage.records);
    }

    return records;
  }

  private async fetchTaxPage(
    page: number,
    filters?: { include_inactive?: boolean }
  ): Promise<{ records: FinanceTaxRecord[]; totalPages: number }> {
    const params = new URLSearchParams();
    params.set('limit', '100');
    params.set('page', String(page));

    if (filters?.include_inactive === true) {
      params.set('include_archived', 'true');
    } else if (filters?.include_inactive === false) {
      params.set('active', 'true');
    }

    const response: any = await firstValueFrom(
      this.restApi.get(`${FinanceEndpoints.FINANCE_GET_TAXES_ENDPOINT}?${params.toString()}`)
    );

    const rows = Array.isArray(response?.taxes)
      ? response.taxes
      : Array.isArray(response?.records)
        ? response.records
      : Array.isArray(response)
        ? response
        : [];

    const records = rows
      .map((row: any) => this.normalizeTax(row))
      .filter((row: FinanceTaxRecord | null): row is FinanceTaxRecord => row !== null);

    return {
      records,
      totalPages: this.responseTotalPages(response),
    };
  }

  async getCurrencies(_filters?: { include_inactive?: boolean }): Promise<FinanceCurrencyRecord[]> {
    // CurrencyService owns the session-scoped activation cache. Operational forms
    // always receive enabled choices; saved codes use its separate preload path.
    return (await this.currencyService.options()).map((option) => ({
      id: option.code, code: option.code, name: option.name,
      symbol: option.symbol || null, company_base_currency: option.is_base,
      active: option.active, active_for_purchases: option.active,
      selectable_on_documents: option.active,
    }));
  }

  async getDefaultCurrencyIso(): Promise<string> {
    return this.resolveDefaultCurrencyIso();
  }

  async getDefaultTaxIds(scope: FinanceTaxScope = 'sales'): Promise<string[]> {
    const defaultTax = await this.getDefaultTax(scope);
    return defaultTax ? [defaultTax.id] : [];
  }

  async getDefaultTax(scope: FinanceTaxScope = 'sales'): Promise<FinanceTaxRecord | null> {
    const taxes = await this.getTaxes({ include_inactive: false });
    const scopedTaxes = taxes
      .filter((tax) => tax.active !== false)
      .filter((tax) => scope === 'both' || tax.scope === scope || tax.scope === 'both')
      .sort((first, second) => first.sequence - second.sequence || first.name.localeCompare(second.name));

    const configuredDefaultIds = await this.getConfiguredDefaultTaxIds(scope).catch(() => []);
    const configuredDefault = configuredDefaultIds
      .map((taxId) => scopedTaxes.find((tax) => tax.id === taxId))
      .find((tax): tax is FinanceTaxRecord => !!tax);

    return configuredDefault ?? scopedTaxes[0] ?? null;
  }

  async calculateTaxes(payload: FinanceTaxCalculationRequest): Promise<FinanceTaxCalculationResult> {
    const response: any = await firstValueFrom(
      this.restApi.post(FinanceEndpoints.FINANCE_POST_TAX_CALCULATION_ENDPOINT, {
        ...payload,
        tax_ids: Array.isArray(payload.tax_ids)
          ? payload.tax_ids.map((taxId) => String(taxId || '').trim()).filter(Boolean)
          : [],
      })
    );
    return this.normalizeTaxCalculationResult(response, payload);
  }

  private async resolveDefaultCurrencyIso(): Promise<string> {
    try {
      const [currencies, settings] = await Promise.all([
        this.getCurrencies({ include_inactive: false }),
        firstValueFrom(this.configurationSettings.all()),
      ]);
      const configuredCurrencyId = this.settingText(settings.values['finance.currency.default_currency']);
      const configuredCurrency = currencies.find((currency) => currency.id === configuredCurrencyId);
      const baseCurrency = currencies.find((currency) => currency.company_base_currency);
      const firstActive = currencies.find((currency) => currency.active);
      const code = configuredCurrency?.code || baseCurrency?.code || firstActive?.code;
      return this.normalizeCurrencyIso(code) || 'USD';
    } catch {
      return 'USD';
    }
  }

  createTax(payload: FinanceTaxPayload) {
    this.clearTaxCache();
    return this.restApi.post(FinanceEndpoints.FINANCE_POST_TAXES_ENDPOINT, payload);
  }

  updateTax(id: string, payload: Partial<FinanceTaxPayload>) {
    this.clearTaxCache();
    return this.restApi.put(formatString(FinanceEndpoints.FINANCE_PUT_TAX_ENDPOINT, id), payload);
  }

  archiveTax(id: string) {
    this.clearTaxCache();
    return firstValueFrom(this.restApi.post(formatString(FinanceEndpoints.FINANCE_POST_TAX_ARCHIVE_ENDPOINT, id), {}));
  }

  restoreTax(id: string) {
    this.clearTaxCache();
    return firstValueFrom(this.restApi.post(formatString(FinanceEndpoints.FINANCE_POST_TAX_RESTORE_ENDPOINT, id), {}));
  }

  deleteTax(id: string) {
    this.clearTaxCache();
    return firstValueFrom(this.restApi.delete(formatString(FinanceEndpoints.FINANCE_DELETE_TAX_ENDPOINT, id)));
  }

  async getCustomers(): Promise<FinanceCustomerRecord[]> {
    const response: any = await firstValueFrom(this.restApi.get(FinanceEndpoints.FINANCE_GET_CUSTOMERS_ENDPOINT));
    const rows = Array.isArray(response?.customers)
      ? response.customers
      : Array.isArray(response?.partners)
        ? response.partners
      : Array.isArray(response)
        ? response
        : [];

    return rows
      .map((row: any) => this.normalizeCustomer(row))
      .filter((row: FinanceCustomerRecord | null): row is FinanceCustomerRecord => row !== null);
  }

  async getPaymentTerms(filters?: { include_inactive?: boolean }): Promise<FinancePaymentTermRecord[]> {
    const params = new URLSearchParams();
    if (typeof filters?.include_inactive === 'boolean') {
      params.set('include_inactive', String(filters.include_inactive));
    }

    const endpoint = params.toString().length > 0 ? `${FinanceEndpoints.FINANCE_GET_PAYMENT_TERMS_ENDPOINT}?${params}` : FinanceEndpoints.FINANCE_GET_PAYMENT_TERMS_ENDPOINT;
    const response: any = await firstValueFrom(this.restApi.get(endpoint));
    const rows = Array.isArray(response?.payment_terms) ? response.payment_terms : Array.isArray(response) ? response : [];

    return rows
      .map((row: any) => this.normalizePaymentTerm(row))
      .filter((row: FinancePaymentTermRecord | null): row is FinancePaymentTermRecord => row !== null);
  }

  createPaymentTerm(payload: FinancePaymentTermPayload) {
    return this.restApi.post(FinanceEndpoints.FINANCE_POST_PAYMENT_TERMS_ENDPOINT, payload);
  }

  updatePaymentTerm(id: string, payload: Partial<FinancePaymentTermPayload>) {
    return this.restApi.put(formatString(FinanceEndpoints.FINANCE_PUT_PAYMENT_TERM_ENDPOINT, id), payload);
  }

  archivePaymentTerm(id: string) {
    return firstValueFrom(this.restApi.post(formatString(FinanceEndpoints.FINANCE_POST_PAYMENT_TERM_ARCHIVE_ENDPOINT, id), {}));
  }

  restorePaymentTerm(id: string) {
    return firstValueFrom(this.restApi.post(formatString(FinanceEndpoints.FINANCE_POST_PAYMENT_TERM_RESTORE_ENDPOINT, id), {}));
  }

  deletePaymentTerm(id: string) {
    return firstValueFrom(this.restApi.delete(formatString(FinanceEndpoints.FINANCE_DELETE_PAYMENT_TERM_ENDPOINT, id)));
  }

  async getFollowUpLevels(filters?: { include_inactive?: boolean }): Promise<FinanceFollowUpLevelRecord[]> {
    const params = new URLSearchParams();
    if (typeof filters?.include_inactive === 'boolean') {
      params.set('include_inactive', String(filters.include_inactive));
    }

    const endpoint = params.toString().length > 0 ? `${FinanceEndpoints.FINANCE_GET_FOLLOW_UP_LEVELS_ENDPOINT}?${params}` : FinanceEndpoints.FINANCE_GET_FOLLOW_UP_LEVELS_ENDPOINT;
    const response: any = await firstValueFrom(this.restApi.get(endpoint));
    const rows = Array.isArray(response?.follow_up_levels) ? response.follow_up_levels : Array.isArray(response) ? response : [];

    return rows
      .map((row: any) => this.normalizeFollowUpLevel(row))
      .filter((row: FinanceFollowUpLevelRecord | null): row is FinanceFollowUpLevelRecord => row !== null);
  }

  createFollowUpLevel(payload: FinanceFollowUpLevelPayload) {
    return this.restApi.post(FinanceEndpoints.FINANCE_POST_FOLLOW_UP_LEVELS_ENDPOINT, payload);
  }

  updateFollowUpLevel(id: string, payload: Partial<FinanceFollowUpLevelPayload>) {
    return this.restApi.put(formatString(FinanceEndpoints.FINANCE_PUT_FOLLOW_UP_LEVEL_ENDPOINT, id), payload);
  }

  archiveFollowUpLevel(id: string) {
    return firstValueFrom(this.restApi.post(formatString(FinanceEndpoints.FINANCE_POST_FOLLOW_UP_LEVEL_ARCHIVE_ENDPOINT, id), {}));
  }

  restoreFollowUpLevel(id: string) {
    return firstValueFrom(this.restApi.post(formatString(FinanceEndpoints.FINANCE_POST_FOLLOW_UP_LEVEL_RESTORE_ENDPOINT, id), {}));
  }

  deleteFollowUpLevel(id: string) {
    return firstValueFrom(this.restApi.delete(formatString(FinanceEndpoints.FINANCE_DELETE_FOLLOW_UP_LEVEL_ENDPOINT, id)));
  }

  async getIncoterms(filters?: { include_inactive?: boolean }): Promise<FinanceIncotermRecord[]> {
    const params = new URLSearchParams();
    if (typeof filters?.include_inactive === 'boolean') {
      params.set('include_inactive', String(filters.include_inactive));
    }

    const endpoint = params.toString().length > 0 ? `${FinanceEndpoints.FINANCE_GET_INCOTERMS_ENDPOINT}?${params}` : FinanceEndpoints.FINANCE_GET_INCOTERMS_ENDPOINT;
    const response: any = await firstValueFrom(this.restApi.get(endpoint));
    const rows = Array.isArray(response?.incoterms) ? response.incoterms : Array.isArray(response) ? response : [];

    return rows
      .map((row: any) => this.normalizeIncoterm(row))
      .filter((row: FinanceIncotermRecord | null): row is FinanceIncotermRecord => row !== null);
  }

  createIncoterm(payload: FinanceIncotermPayload) {
    return this.restApi.post(FinanceEndpoints.FINANCE_POST_INCOTERMS_ENDPOINT, payload);
  }

  updateIncoterm(id: string, payload: Partial<FinanceIncotermPayload>) {
    return this.restApi.put(formatString(FinanceEndpoints.FINANCE_PUT_INCOTERM_ENDPOINT, id), payload);
  }

  archiveIncoterm(id: string) {
    return firstValueFrom(this.restApi.post(formatString(FinanceEndpoints.FINANCE_POST_INCOTERM_ARCHIVE_ENDPOINT, id), {}));
  }

  restoreIncoterm(id: string) {
    return firstValueFrom(this.restApi.post(formatString(FinanceEndpoints.FINANCE_POST_INCOTERM_RESTORE_ENDPOINT, id), {}));
  }

  deleteIncoterm(id: string) {
    return firstValueFrom(this.restApi.delete(formatString(FinanceEndpoints.FINANCE_DELETE_INCOTERM_ENDPOINT, id)));
  }

  async getBankAccounts(filters?: { include_inactive?: boolean }): Promise<FinanceBankAccountRecord[]> {
    const params = new URLSearchParams();
    if (typeof filters?.include_inactive === 'boolean') {
      params.set('include_inactive', String(filters.include_inactive));
    }

    const endpoint = params.toString().length > 0 ? `${this.bankAccountsEndpoint}?${params}` : this.bankAccountsEndpoint;
    const response: any = await firstValueFrom(this.restApi.get(endpoint));
    const rows = Array.isArray(response?.bank_accounts) ? response.bank_accounts : Array.isArray(response) ? response : [];

    return rows
      .map((row: any) => this.normalizeBankAccount(row))
      .filter((row: FinanceBankAccountRecord | null): row is FinanceBankAccountRecord => row !== null);
  }

  createBankAccount(payload: FinanceBankAccountPayload) {
    return this.restApi.post(this.bankAccountsEndpoint, payload);
  }

  updateBankAccount(id: string, payload: Partial<FinanceBankAccountPayload>) {
    return this.restApi.put(`${this.bankAccountsEndpoint}/${id}`, payload);
  }

  archiveBankAccount(id: string) {
    return firstValueFrom(this.restApi.post(`${this.bankAccountsEndpoint}/${id}/archive`, {}));
  }

  restoreBankAccount(id: string) {
    return firstValueFrom(this.restApi.post(`${this.bankAccountsEndpoint}/${id}/restore`, {}));
  }

  deleteBankAccount(id: string) {
    return firstValueFrom(this.restApi.delete(`${this.bankAccountsEndpoint}/${id}`));
  }

  async getReconciliationModels(filters?: { include_inactive?: boolean }): Promise<FinanceReconciliationModelRecord[]> {
    const params = new URLSearchParams();
    if (typeof filters?.include_inactive === 'boolean') {
      params.set('include_inactive', String(filters.include_inactive));
    }

    const endpoint = params.toString().length > 0
      ? `${this.reconciliationModelsEndpoint}?${params}`
      : this.reconciliationModelsEndpoint;
    const response: any = await firstValueFrom(this.restApi.get(endpoint));
    const rows = Array.isArray(response?.reconciliation_models)
      ? response.reconciliation_models
      : Array.isArray(response)
        ? response
        : [];

    return rows
      .map((row: any) => this.normalizeReconciliationModel(row))
      .filter(
        (row: FinanceReconciliationModelRecord | null): row is FinanceReconciliationModelRecord => row !== null
      );
  }

  createReconciliationModel(payload: FinanceReconciliationModelPayload) {
    return this.restApi.post(this.reconciliationModelsEndpoint, payload);
  }

  updateReconciliationModel(id: string, payload: Partial<FinanceReconciliationModelPayload>) {
    return this.restApi.put(`${this.reconciliationModelsEndpoint}/${id}`, payload);
  }

  archiveReconciliationModel(id: string) {
    return firstValueFrom(this.restApi.post(`${this.reconciliationModelsEndpoint}/${id}/archive`, {}));
  }

  restoreReconciliationModel(id: string) {
    return firstValueFrom(this.restApi.post(`${this.reconciliationModelsEndpoint}/${id}/restore`, {}));
  }

  deleteReconciliationModel(id: string) {
    return firstValueFrom(this.restApi.delete(`${this.reconciliationModelsEndpoint}/${id}`));
  }

  async getJournalEntriesSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_JOURNAL_ENTRIES_SCHEMA_ENDPOINT)
    );
  }

  async getJournalEntriesOptions(): Promise<Record<string, any>> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_JOURNAL_ENTRIES_OPTIONS_ENDPOINT)
    ) as Promise<Record<string, any>>;
  }

  async getJournalEntriesPage(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationPageResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_JOURNAL_ENTRIES_PAGE_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationPageResponse(response, 'journal-entries', query);
  }

  async runDueJournalEntries(): Promise<{ posted: number; failed: number; errors: Array<{ id: string; message: string }> }> {
    return firstValueFrom(this.restApi.post(FinanceEndpoints.FINANCE_POST_JOURNAL_ENTRIES_RUN_DUE_ENDPOINT, {})) as Promise<{ posted: number; failed: number; errors: Array<{ id: string; message: string }> }>;
  }

  createJournalEntry(payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.post(FinanceEndpoints.FINANCE_POST_JOURNAL_ENTRY_ENDPOINT, payload)
    );
  }

  getJournalEntry(id: string) {
    return firstValueFrom(
      this.restApi.get(
        formatString(FinanceEndpoints.FINANCE_GET_JOURNAL_ENTRY_ENDPOINT, id)
      )
    );
  }

  updateJournalEntry(id: string, payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.put(
        formatString(FinanceEndpoints.FINANCE_PUT_JOURNAL_ENTRY_ENDPOINT, id),
        payload
      )
    );
  }

  runJournalEntryAction(
    id: string,
    action: string,
    payload: Record<string, any> = {}
  ) {
    return firstValueFrom(
      this.restApi.post(
        formatString(
          FinanceEndpoints.FINANCE_POST_JOURNAL_ENTRY_ACTION_ENDPOINT,
          id,
          action
        ),
        payload
      )
    );
  }

  deleteJournalEntry(id: string) {
    return firstValueFrom(
      this.restApi.delete(
        formatString(FinanceEndpoints.FINANCE_DELETE_JOURNAL_ENTRY_ENDPOINT, id)
      )
    );
  }

  async getJournalItemsSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_JOURNAL_ITEMS_SCHEMA_ENDPOINT)
    );
  }

  async getJournalItemsOptions(): Promise<Record<string, any>> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_JOURNAL_ITEMS_OPTIONS_ENDPOINT)
    ) as Promise<Record<string, any>>;
  }

  async getJournalItemsPage(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationPageResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_JOURNAL_ITEMS_PAGE_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationPageResponse(response, 'journal-items', query);
  }

  createJournalItem(payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.post(FinanceEndpoints.FINANCE_POST_JOURNAL_ITEM_ENDPOINT, payload)
    );
  }

  getJournalItem(id: string) {
    return firstValueFrom(
      this.restApi.get(
        formatString(FinanceEndpoints.FINANCE_GET_JOURNAL_ITEM_ENDPOINT, id)
      )
    );
  }

  updateJournalItem(id: string, payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.put(
        formatString(FinanceEndpoints.FINANCE_PUT_JOURNAL_ITEM_ENDPOINT, id),
        payload
      )
    );
  }

  runJournalItemAction(
    id: string,
    action: string,
    payload: Record<string, any> = {}
  ) {
    return firstValueFrom(
      this.restApi.post(
        formatString(
          FinanceEndpoints.FINANCE_POST_JOURNAL_ITEM_ACTION_ENDPOINT,
          id,
          action
        ),
        payload
      )
    );
  }

  deleteJournalItem(id: string) {
    return firstValueFrom(
      this.restApi.delete(
        formatString(FinanceEndpoints.FINANCE_DELETE_JOURNAL_ITEM_ENDPOINT, id)
      )
    );
  }

  async getReconcileSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_RECONCILE_SCHEMA_ENDPOINT)
    );
  }

  async getReconcileOptions(): Promise<Record<string, any>> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_RECONCILE_OPTIONS_ENDPOINT)
    ) as Promise<Record<string, any>>;
  }

  async getReconcilePage(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationPageResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_RECONCILE_PAGE_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationPageResponse(response, 'reconcile', query);
  }

  createReconcileRecord(payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.post(FinanceEndpoints.FINANCE_POST_RECONCILE_RECORD_ENDPOINT, payload)
    );
  }

  getReconcileRecord(id: string) {
    return firstValueFrom(
      this.restApi.get(
        formatString(FinanceEndpoints.FINANCE_GET_RECONCILE_RECORD_ENDPOINT, id)
      )
    );
  }

  updateReconcileRecord(id: string, payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.put(
        formatString(FinanceEndpoints.FINANCE_PUT_RECONCILE_RECORD_ENDPOINT, id),
        payload
      )
    );
  }

  runReconcileRecordAction(
    id: string,
    action: string,
    payload: Record<string, any> = {}
  ) {
    return firstValueFrom(
      this.restApi.post(
        formatString(
          FinanceEndpoints.FINANCE_POST_RECONCILE_RECORD_ACTION_ENDPOINT,
          id,
          action
        ),
        payload
      )
    );
  }

  deleteReconcileRecord(id: string) {
    return firstValueFrom(
      this.restApi.delete(
        formatString(FinanceEndpoints.FINANCE_DELETE_RECONCILE_RECORD_ENDPOINT, id)
      )
    );
  }

  async getFixedAssetsSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_FIXED_ASSETS_SCHEMA_ENDPOINT)
    );
  }

  async getFixedAssetsOptions(): Promise<Record<string, any>> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_FIXED_ASSETS_OPTIONS_ENDPOINT)
    ) as Promise<Record<string, any>>;
  }

  async getFixedAssetsPage(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationPageResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_FIXED_ASSETS_PAGE_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationPageResponse(response, 'fixed-assets', query);
  }

  createFixedAsset(payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.post(FinanceEndpoints.FINANCE_POST_FIXED_ASSET_ENDPOINT, payload)
    );
  }

  getFixedAsset(id: string) {
    return firstValueFrom(
      this.restApi.get(
        formatString(FinanceEndpoints.FINANCE_GET_FIXED_ASSET_ENDPOINT, id)
      )
    );
  }

  updateFixedAsset(id: string, payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.put(
        formatString(FinanceEndpoints.FINANCE_PUT_FIXED_ASSET_ENDPOINT, id),
        payload
      )
    );
  }

  runFixedAssetAction(
    id: string,
    action: string,
    payload: Record<string, any> = {}
  ) {
    return firstValueFrom(
      this.restApi.post(
        formatString(
          FinanceEndpoints.FINANCE_POST_FIXED_ASSET_ACTION_ENDPOINT,
          id,
          action
        ),
        payload
      )
    );
  }

  deleteFixedAsset(id: string) {
    return firstValueFrom(
      this.restApi.delete(
        formatString(FinanceEndpoints.FINANCE_DELETE_FIXED_ASSET_ENDPOINT, id)
      )
    );
  }

  async getDeferredRevenuesSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_DEFERRED_REVENUES_SCHEMA_ENDPOINT)
    );
  }

  async getDeferredRevenuesOptions(): Promise<Record<string, any>> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_DEFERRED_REVENUES_OPTIONS_ENDPOINT)
    ) as Promise<Record<string, any>>;
  }

  async getDeferredRevenuesPage(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationPageResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_DEFERRED_REVENUES_PAGE_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationPageResponse(response, 'deferred-revenues', query);
  }

  createDeferredRevenue(payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.post(FinanceEndpoints.FINANCE_POST_DEFERRED_REVENUE_ENDPOINT, payload)
    );
  }

  getDeferredRevenue(id: string) {
    return firstValueFrom(
      this.restApi.get(
        formatString(FinanceEndpoints.FINANCE_GET_DEFERRED_REVENUE_ENDPOINT, id)
      )
    );
  }

  updateDeferredRevenue(id: string, payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.put(
        formatString(FinanceEndpoints.FINANCE_PUT_DEFERRED_REVENUE_ENDPOINT, id),
        payload
      )
    );
  }

  runDeferredRevenueAction(
    id: string,
    action: string,
    payload: Record<string, any> = {}
  ) {
    return firstValueFrom(
      this.restApi.post(
        formatString(
          FinanceEndpoints.FINANCE_POST_DEFERRED_REVENUE_ACTION_ENDPOINT,
          id,
          action
        ),
        payload
      )
    );
  }

  deleteDeferredRevenue(id: string) {
    return firstValueFrom(
      this.restApi.delete(
        formatString(FinanceEndpoints.FINANCE_DELETE_DEFERRED_REVENUE_ENDPOINT, id)
      )
    );
  }

  async getDeferredExpensesSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_DEFERRED_EXPENSES_SCHEMA_ENDPOINT)
    );
  }

  async getDeferredExpensesOptions(): Promise<Record<string, any>> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_DEFERRED_EXPENSES_OPTIONS_ENDPOINT)
    ) as Promise<Record<string, any>>;
  }

  async getDeferredExpensesPage(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationPageResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_DEFERRED_EXPENSES_PAGE_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationPageResponse(response, 'deferred-expenses', query);
  }

  createDeferredExpense(payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.post(FinanceEndpoints.FINANCE_POST_DEFERRED_EXPENSE_ENDPOINT, payload)
    );
  }

  getDeferredExpense(id: string) {
    return firstValueFrom(
      this.restApi.get(
        formatString(FinanceEndpoints.FINANCE_GET_DEFERRED_EXPENSE_ENDPOINT, id)
      )
    );
  }

  updateDeferredExpense(id: string, payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.put(
        formatString(FinanceEndpoints.FINANCE_PUT_DEFERRED_EXPENSE_ENDPOINT, id),
        payload
      )
    );
  }

  runDeferredExpenseAction(
    id: string,
    action: string,
    payload: Record<string, any> = {}
  ) {
    return firstValueFrom(
      this.restApi.post(
        formatString(
          FinanceEndpoints.FINANCE_POST_DEFERRED_EXPENSE_ACTION_ENDPOINT,
          id,
          action
        ),
        payload
      )
    );
  }

  deleteDeferredExpense(id: string) {
    return firstValueFrom(
      this.restApi.delete(
        formatString(FinanceEndpoints.FINANCE_DELETE_DEFERRED_EXPENSE_ENDPOINT, id)
      )
    );
  }

  async getAnalyticItemsSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_ANALYTIC_ITEMS_SCHEMA_ENDPOINT)
    );
  }

  async getAnalyticItemsOptions(): Promise<Record<string, any>> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_ANALYTIC_ITEMS_OPTIONS_ENDPOINT)
    ) as Promise<Record<string, any>>;
  }

  async getAnalyticItemsPage(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationPageResponse> {
    const params = new URLSearchParams(this.operationQuery(query).slice(1));
    for (const filter of query.searchPayload?.filters ?? []) {
      params.delete(filter.key); params.delete(`${filter.key}_from`); params.delete(`${filter.key}_to`);
    }
    // Preserve typed operators/ranges instead of flattening them to equality-only query strings.
    params.set('filters', JSON.stringify((query.searchPayload?.filters ?? []).map(filter => ({ key: filter.column, operator: filter.operator, value: filter.value }))));
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_ANALYTIC_ITEMS_PAGE_ENDPOINT}?${params.toString()}`
      )
    );
    return this.normalizeOperationPageResponse(response, 'analytic-items', query);
  }

  createAnalyticItem(payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.post(FinanceEndpoints.FINANCE_POST_ANALYTIC_ITEM_ENDPOINT, payload)
    );
  }

  getAnalyticItem(id: string) {
    return firstValueFrom(
      this.restApi.get(
        formatString(FinanceEndpoints.FINANCE_GET_ANALYTIC_ITEM_ENDPOINT, id)
      )
    );
  }

  updateAnalyticItem(id: string, payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.put(
        formatString(FinanceEndpoints.FINANCE_PUT_ANALYTIC_ITEM_ENDPOINT, id),
        payload
      )
    );
  }

  runAnalyticItemAction(
    id: string,
    action: string,
    payload: Record<string, any> = {}
  ) {
    return firstValueFrom(
      this.restApi.post(
        formatString(
          FinanceEndpoints.FINANCE_POST_ANALYTIC_ITEM_ACTION_ENDPOINT,
          id,
          action
        ),
        payload
      )
    );
  }

  deleteAnalyticItem(id: string) {
    return firstValueFrom(
      this.restApi.delete(
        formatString(FinanceEndpoints.FINANCE_DELETE_ANALYTIC_ITEM_ENDPOINT, id)
      )
    );
  }

  async getBankStatementsSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_BANK_STATEMENTS_SCHEMA_ENDPOINT)
    );
  }

  async getBankStatementsOptions(): Promise<Record<string, any>> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_BANK_STATEMENTS_OPTIONS_ENDPOINT)
    ) as Promise<Record<string, any>>;
  }

  async getBankStatementsPage(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationPageResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_BANK_STATEMENTS_PAGE_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationPageResponse(response, 'bank-statements', query);
  }

  createBankStatement(payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.post(FinanceEndpoints.FINANCE_POST_BANK_STATEMENT_ENDPOINT, payload)
    );
  }

  getBankStatement(id: string) {
    return firstValueFrom(
      this.restApi.get(
        formatString(FinanceEndpoints.FINANCE_GET_BANK_STATEMENT_ENDPOINT, id)
      )
    );
  }

  updateBankStatement(id: string, payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.put(
        formatString(FinanceEndpoints.FINANCE_PUT_BANK_STATEMENT_ENDPOINT, id),
        payload
      )
    );
  }

  runBankStatementAction(
    id: string,
    action: string,
    payload: Record<string, any> = {}
  ) {
    return firstValueFrom(
      this.restApi.post(
        formatString(
          FinanceEndpoints.FINANCE_POST_BANK_STATEMENT_ACTION_ENDPOINT,
          id,
          action
        ),
        payload
      )
    );
  }

  deleteBankStatement(id: string) {
    return firstValueFrom(
      this.restApi.delete(
        formatString(FinanceEndpoints.FINANCE_DELETE_BANK_STATEMENT_ENDPOINT, id)
      )
    );
  }

  async getReconciliationSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_RECONCILIATION_SCHEMA_ENDPOINT)
    );
  }

  async getReconciliationOptions(): Promise<Record<string, any>> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_RECONCILIATION_OPTIONS_ENDPOINT)
    ) as Promise<Record<string, any>>;
  }

  async getReconciliationPage(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationPageResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_RECONCILIATION_PAGE_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationPageResponse(response, 'reconciliation', query);
  }

  createReconciliationRecord(payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.post(FinanceEndpoints.FINANCE_POST_RECONCILIATION_RECORD_ENDPOINT, payload)
    );
  }

  getReconciliationRecord(id: string) {
    return firstValueFrom(
      this.restApi.get(
        formatString(FinanceEndpoints.FINANCE_GET_RECONCILIATION_RECORD_ENDPOINT, id)
      )
    );
  }

  updateReconciliationRecord(id: string, payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.put(
        formatString(FinanceEndpoints.FINANCE_PUT_RECONCILIATION_RECORD_ENDPOINT, id),
        payload
      )
    );
  }

  runReconciliationRecordAction(
    id: string,
    action: string,
    payload: Record<string, any> = {}
  ) {
    return firstValueFrom(
      this.restApi.post(
        formatString(
          FinanceEndpoints.FINANCE_POST_RECONCILIATION_RECORD_ACTION_ENDPOINT,
          id,
          action
        ),
        payload
      )
    );
  }

  deleteReconciliationRecord(id: string) {
    return firstValueFrom(
      this.restApi.delete(
        formatString(FinanceEndpoints.FINANCE_DELETE_RECONCILIATION_RECORD_ENDPOINT, id)
      )
    );
  }

  async getBatchPaymentsSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_BATCH_PAYMENTS_SCHEMA_ENDPOINT)
    );
  }

  async getBatchPaymentsOptions(): Promise<Record<string, any>> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_BATCH_PAYMENTS_OPTIONS_ENDPOINT)
    ) as Promise<Record<string, any>>;
  }

  async getBatchPaymentsPage(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationPageResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_BATCH_PAYMENTS_PAGE_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationPageResponse(response, 'batch-payments', query);
  }

  async getActiveBatchPaymentRules(): Promise<Record<string, any>[]> {
    const response = await firstValueFrom(this.restApi.get<{ records: Record<string, any>[] }>(
      FinanceEndpoints.FINANCE_GET_BATCH_PAYMENT_RULES_ENDPOINT + '?active=true&per_page=100'
    ));
    return response.records;
  }

  previewBatchPayment(payload: Record<string, unknown>): Promise<Record<string, any>> {
    return firstValueFrom(this.restApi.post<Record<string, any>>(FinanceEndpoints.FINANCE_PREVIEW_BATCH_PAYMENT_ENDPOINT, payload));
  }

  createBatchPayment(payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.post(FinanceEndpoints.FINANCE_POST_BATCH_PAYMENT_ENDPOINT, payload)
    );
  }

  getBatchPayment(id: string) {
    return firstValueFrom(
      this.restApi.get(
        formatString(FinanceEndpoints.FINANCE_GET_BATCH_PAYMENT_ENDPOINT, id)
      )
    );
  }

  updateBatchPayment(id: string, payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.put(
        formatString(FinanceEndpoints.FINANCE_PUT_BATCH_PAYMENT_ENDPOINT, id),
        payload
      )
    );
  }

  runBatchPaymentAction(
    id: string,
    action: string,
    payload: Record<string, any> = {}
  ) {
    return firstValueFrom(
      this.restApi.post(
        formatString(
          FinanceEndpoints.FINANCE_POST_BATCH_PAYMENT_ACTION_ENDPOINT,
          id,
          action
        ),
        payload
      )
    );
  }

  deleteBatchPayment(id: string) {
    return firstValueFrom(
      this.restApi.delete(
        formatString(FinanceEndpoints.FINANCE_DELETE_BATCH_PAYMENT_ENDPOINT, id)
      )
    );
  }

  async getCustomerPaymentsSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_CUSTOMER_PAYMENTS_SCHEMA_ENDPOINT)
    );
  }

  async getCustomerPaymentsOptions(): Promise<Record<string, any>> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_CUSTOMER_PAYMENTS_OPTIONS_ENDPOINT)
    ) as Promise<Record<string, any>>;
  }

  async getPaymentMethodsForBranch(branchId: string, direction: 'inbound' | 'outbound'): Promise<Array<Record<string, any>>> {
    const query = new URLSearchParams({ branch_id: branchId, direction, limit: '100' });
    const response = await firstValueFrom(this.restApi.get<{ options?: Array<Record<string, any>> }>(
      `${FinanceEndpoints.FINANCE_GET_PAYMENT_METHODS_ENDPOINT}/options?${query.toString()}`,
    ));
    return response.options ?? [];
  }

  async getCustomerPaymentsPage(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationPageResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_CUSTOMER_PAYMENTS_PAGE_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationPageResponse(response, 'customer-payments', query);
  }

  createCustomerPayment(payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.post(FinanceEndpoints.FINANCE_POST_CUSTOMER_PAYMENT_ENDPOINT, payload)
    );
  }

  getCustomerPayment(id: string) {
    return firstValueFrom(
      this.restApi.get(
        formatString(FinanceEndpoints.FINANCE_GET_CUSTOMER_PAYMENT_ENDPOINT, id)
      )
    );
  }

  updateCustomerPayment(id: string, payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.put(
        formatString(FinanceEndpoints.FINANCE_PUT_CUSTOMER_PAYMENT_ENDPOINT, id),
        payload
      )
    );
  }

  runCustomerPaymentAction(
    id: string,
    action: string,
    payload: Record<string, any> = {}
  ) {
    return firstValueFrom(
      this.restApi.post(
        formatString(
          FinanceEndpoints.FINANCE_POST_CUSTOMER_PAYMENT_ACTION_ENDPOINT,
          id,
          action
        ),
        payload
      )
    );
  }

  deleteCustomerPayment(id: string) {
    return firstValueFrom(
      this.restApi.delete(
        formatString(FinanceEndpoints.FINANCE_DELETE_CUSTOMER_PAYMENT_ENDPOINT, id)
      )
    );
  }

  async getVendorPaymentsSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_VENDOR_PAYMENTS_SCHEMA_ENDPOINT)
    );
  }

  async getVendorPaymentsOptions(): Promise<Record<string, any>> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_VENDOR_PAYMENTS_OPTIONS_ENDPOINT)
    ) as Promise<Record<string, any>>;
  }

  async getVendorPaymentsPage(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationPageResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_VENDOR_PAYMENTS_PAGE_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationPageResponse(response, 'vendor-payments', query);
  }

  createVendorPayment(payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.post(FinanceEndpoints.FINANCE_POST_VENDOR_PAYMENT_ENDPOINT, payload)
    );
  }

  getVendorPayment(id: string) {
    return firstValueFrom(
      this.restApi.get(
        formatString(FinanceEndpoints.FINANCE_GET_VENDOR_PAYMENT_ENDPOINT, id)
      )
    );
  }

  updateVendorPayment(id: string, payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.put(
        formatString(FinanceEndpoints.FINANCE_PUT_VENDOR_PAYMENT_ENDPOINT, id),
        payload
      )
    );
  }

  runVendorPaymentAction(
    id: string,
    action: string,
    payload: Record<string, any> = {}
  ) {
    return firstValueFrom(
      this.restApi.post(
        formatString(
          FinanceEndpoints.FINANCE_POST_VENDOR_PAYMENT_ACTION_ENDPOINT,
          id,
          action
        ),
        payload
      )
    );
  }

  deleteVendorPayment(id: string) {
    return firstValueFrom(
      this.restApi.delete(
        formatString(FinanceEndpoints.FINANCE_DELETE_VENDOR_PAYMENT_ENDPOINT, id)
      )
    );
  }

  async getAccountingTransfersSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_ACCOUNTING_TRANSFERS_SCHEMA_ENDPOINT)
    );
  }

  async getAccountingTransfersOptions(): Promise<Record<string, any>> {
    return firstValueFrom(
      this.restApi.get(FinanceEndpoints.FINANCE_GET_ACCOUNTING_TRANSFERS_OPTIONS_ENDPOINT)
    ) as Promise<Record<string, any>>;
  }

  async getAccountingTransfersPage(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationPageResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_ACCOUNTING_TRANSFERS_PAGE_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationPageResponse(response, 'accounting-transfers', query);
  }

  createAccountingTransfer(payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.post(FinanceEndpoints.FINANCE_POST_ACCOUNTING_TRANSFER_ENDPOINT, payload)
    );
  }

  getAccountingTransfer(id: string) {
    return firstValueFrom(
      this.restApi.get(
        formatString(FinanceEndpoints.FINANCE_GET_ACCOUNTING_TRANSFER_ENDPOINT, id)
      )
    );
  }

  updateAccountingTransfer(id: string, payload: Record<string, any>) {
    return firstValueFrom(
      this.restApi.put(
        formatString(FinanceEndpoints.FINANCE_PUT_ACCOUNTING_TRANSFER_ENDPOINT, id),
        payload
      )
    );
  }

  runAccountingTransferAction(
    id: string,
    action: string,
    payload: Record<string, any> = {}
  ) {
    return firstValueFrom(
      this.restApi.post(
        formatString(
          FinanceEndpoints.FINANCE_POST_ACCOUNTING_TRANSFER_ACTION_ENDPOINT,
          id,
          action
        ),
        payload
      )
    );
  }

  deleteAccountingTransfer(id: string) {
    return firstValueFrom(
      this.restApi.delete(
        formatString(FinanceEndpoints.FINANCE_DELETE_ACCOUNTING_TRANSFER_ENDPOINT, id)
      )
    );
  }

  getJournalEntryItems(id: string): Promise<{ items: Array<Record<string, any>> }> {
    return firstValueFrom(
      this.restApi.get<{ items: Array<Record<string, any>> }>(
        formatString(FinanceEndpoints.FINANCE_GET_JOURNAL_ENTRY_ITEMS_ENDPOINT, id)
      )
    );
  }

  async previewJournalFromBusinessDocument(
    ownerType: 'finance' | 'sales' | 'procurement' | 'inventory',
    ownerRecordId: string,
  ): Promise<Record<string, any>> {
    return await firstValueFrom(
      this.restApi.get(
        formatString(FinanceEndpoints.FINANCE_GET_JOURNAL_FROM_BUSINESS_DOCUMENT_ENDPOINT, ownerType, ownerRecordId),
      ),
    ) as Record<string, any>;
  }

  async getProfitLossSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(
        FinanceEndpoints.FINANCE_GET_PROFIT_LOSS_REPORT_SCHEMA_ENDPOINT
      )
    );
  }

  async getProfitLoss(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationReportResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_PROFIT_LOSS_REPORT_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationReportResponse(response, 'profit-loss');
  }

  async getBalanceSheetSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(
        FinanceEndpoints.FINANCE_GET_BALANCE_SHEET_REPORT_SCHEMA_ENDPOINT
      )
    );
  }

  async getBalanceSheet(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationReportResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_BALANCE_SHEET_REPORT_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationReportResponse(response, 'balance-sheet');
  }

  async getCashFlowSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(
        FinanceEndpoints.FINANCE_GET_CASH_FLOW_REPORT_SCHEMA_ENDPOINT
      )
    );
  }

  async getCashFlow(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationReportResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_CASH_FLOW_REPORT_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationReportResponse(response, 'cash-flow');
  }

  async getExecutiveSummarySchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(
        FinanceEndpoints.FINANCE_GET_EXECUTIVE_SUMMARY_REPORT_SCHEMA_ENDPOINT
      )
    );
  }

  async getExecutiveSummary(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationReportResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_EXECUTIVE_SUMMARY_REPORT_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationReportResponse(response, 'executive-summary');
  }

  async getGeneralLedgerSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(
        FinanceEndpoints.FINANCE_GET_GENERAL_LEDGER_REPORT_SCHEMA_ENDPOINT
      )
    );
  }

  async getGeneralLedger(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationReportResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_GENERAL_LEDGER_REPORT_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationReportResponse(response, 'general-ledger');
  }

  async getTrialBalanceSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(
        FinanceEndpoints.FINANCE_GET_TRIAL_BALANCE_REPORT_SCHEMA_ENDPOINT
      )
    );
  }

  async getTrialBalance(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationReportResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_TRIAL_BALANCE_REPORT_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationReportResponse(response, 'trial-balance');
  }

  async getJournalReportSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(
        FinanceEndpoints.FINANCE_GET_JOURNAL_REPORT_SCHEMA_ENDPOINT
      )
    );
  }

  async getJournalReport(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationReportResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_JOURNAL_REPORT_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationReportResponse(response, 'journal-report');
  }

  async getTaxAuditSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(
        FinanceEndpoints.FINANCE_GET_TAX_AUDIT_REPORT_SCHEMA_ENDPOINT
      )
    );
  }

  async getTaxAudit(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationReportResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_TAX_AUDIT_REPORT_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationReportResponse(response, 'tax-audit');
  }

  async getPartnerLedgerSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(
        FinanceEndpoints.FINANCE_GET_PARTNER_LEDGER_REPORT_SCHEMA_ENDPOINT
      )
    );
  }

  async getPartnerLedger(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationReportResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_PARTNER_LEDGER_REPORT_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationReportResponse(response, 'partner-ledger');
  }

  async getAgedReceivablesSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(
        FinanceEndpoints.FINANCE_GET_AGED_RECEIVABLES_REPORT_SCHEMA_ENDPOINT
      )
    );
  }

  async getAgedReceivables(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationReportResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_AGED_RECEIVABLES_REPORT_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationReportResponse(response, 'aged-receivables');
  }

  async getAgedPayablesSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(
        FinanceEndpoints.FINANCE_GET_AGED_PAYABLES_REPORT_SCHEMA_ENDPOINT
      )
    );
  }

  async getAgedPayables(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationReportResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_AGED_PAYABLES_REPORT_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationReportResponse(response, 'aged-payables');
  }

  async getFollowUpReportSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(
        FinanceEndpoints.FINANCE_GET_FOLLOW_UP_REPORT_SCHEMA_ENDPOINT
      )
    );
  }

  async getFollowUpReport(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationReportResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_FOLLOW_UP_REPORT_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationReportResponse(response, 'follow-up-report');
  }

  async getTaxReportSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(
        FinanceEndpoints.FINANCE_GET_TAX_REPORT_SCHEMA_ENDPOINT
      )
    );
  }

  async getTaxReport(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationReportResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_TAX_REPORT_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationReportResponse(response, 'tax-report');
  }

  async getVatReturnSchema(): Promise<any> {
    return firstValueFrom(
      this.restApi.get(
        FinanceEndpoints.FINANCE_GET_VAT_RETURN_REPORT_SCHEMA_ENDPOINT
      )
    );
  }

  async getVatReturn(
    query: FinanceOperationListQuery = {}
  ): Promise<FinanceOperationReportResponse> {
    const response: any = await firstValueFrom(
      this.restApi.get(
        `${FinanceEndpoints.FINANCE_GET_VAT_RETURN_REPORT_ENDPOINT}${this.operationQuery(query)}`
      )
    );
    return this.normalizeOperationReportResponse(response, 'vat-return');
  }

  private normalizeOperationPageResponse(
    response: any,
    page: string,
    query: FinanceOperationListQuery
  ): FinanceOperationPageResponse {
    return {
      page: this.asString(response?.page) || page,
      title: this.asString(response?.title),
      records: Array.isArray(response?.records) ? response.records : [],
      summary: response?.summary && typeof response.summary === 'object' ? response.summary : {},
      pagination: {
        page: this.asNumber(response?.pagination?.page, query.page ?? 1),
        per_page: this.asNumber(response?.pagination?.per_page, query.perPage ?? 25),
        total: this.asNumber(response?.pagination?.total, 0),
        total_pages: this.asNumber(response?.pagination?.total_pages, 0),
      },
    };
  }

  private normalizeOperationReportResponse(
    response: any,
    report: string
  ): FinanceOperationReportResponse {
    return {
      report: this.asString(response?.report) || report,
      title: this.asString(response?.title),
      summary: response?.summary && typeof response.summary === 'object' ? response.summary : {},
      lines: Array.isArray(response?.lines) ? response.lines : [],
      options: response?.options,
      warnings: Array.isArray(response?.warnings) ? response.warnings : [],
      features: response?.features && typeof response.features === 'object' ? response.features : {},
      generated_at: this.asString(response?.generated_at),
      data_source: this.asString(response?.data_source),
    };
  }
  private normalizeTaxCalculationResult(
    value: any,
    request: FinanceTaxCalculationRequest
  ): FinanceTaxCalculationResult {
    const baseAmount = this.asNumber(value?.base_amount, request.base_amount);
    const taxAmount = this.asNumber(value?.tax_amount, 0);
    const subtotal = this.asNumber(value?.subtotal ?? value?.untaxed_amount, baseAmount);
    const total = this.asNumber(value?.total, subtotal + taxAmount);

    return {
      currency: this.asNullableString(value?.currency ?? request.currency),
      base_amount: baseAmount,
      subtotal,
      untaxed_amount: this.asNumber(value?.untaxed_amount, subtotal),
      included_tax_amount: this.asNumber(value?.included_tax_amount, 0),
      excluded_tax_amount: this.asNumber(value?.excluded_tax_amount, taxAmount),
      tax_amount: taxAmount,
      total,
      tax_rate: this.asNumber(value?.tax_rate, subtotal > 0 ? taxAmount / subtotal : 0),
      tax_ids: this.asStringArray(value?.tax_ids),
      default_tax_ids: this.asStringArray(value?.default_tax_ids),
      lines: Array.isArray(value?.lines)
        ? value.lines.map((line: any) => this.normalizeTaxCalculationLine(line))
        : [],
      warnings: this.asStringArray(value?.warnings),
    };
  }

  private normalizeTaxCalculationLine(value: any): FinanceTaxCalculationLine {
    return {
      tax_id: this.asNullableString(value?.tax_id),
      name: this.asString(value?.name) || 'Tax',
      amount_type: this.normalizeTaxAmountType(value?.amount_type) || 'percentage',
      base_amount: this.asNumber(value?.base_amount, 0),
      tax_amount: this.asNumber(value?.tax_amount, 0),
      included_in_price: this.asBoolean(value?.included_in_price),
      sequence: this.asNumber(value?.sequence, 0),
    };
  }

  private async getConfiguredDefaultTaxIds(scope: FinanceTaxScope): Promise<string[]> {
    const settings: any = await this.getSettings();
    const salesTaxId = this.settingText(settings?.default_sales_tax);
    const purchaseTaxId = this.settingText(settings?.default_purchase_tax);

    if (scope === 'purchase') {
      return purchaseTaxId ? [purchaseTaxId] : [];
    }

    if (scope === 'sales') {
      return salesTaxId ? [salesTaxId] : [];
    }

    return [salesTaxId, purchaseTaxId].filter(Boolean);
  }

  private settingText(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }
    return value.trim();
  }

  private normalizeTax(value: any): FinanceTaxRecord | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const id = this.asString(value.id);
    const name = this.asString(value.name);
    const taxType = this.asString(value.tax_type) || this.asString(value.tax_category) || 'tax';
    const scope = normalizeFinanceTaxScope(value.scope, value.tax_type, value.tax_scope);
    const amountType = this.normalizeTaxAmountType(value.amount_type) || 'percentage';

    if (!id || !name || !taxType || !scope || !amountType) {
      return null;
    }

    const includedPolicy = this.asString(value.included_in_price).toLowerCase().replace(/[_\s-]+/g, '');

    return {
      id,
      name,
      tax_type: taxType,
      scope,
      amount_type: amountType,
      amount: this.asNumber(value.amount ?? value.tax_amount, 0),
      include_in_price:
        this.asBoolean(value.include_in_price ?? value.price_include) ||
        includedPolicy === 'includedinprice' ||
        includedPolicy === 'priceincluded',
      included_in_price: this.asNullableString(value.included_in_price),
      active: this.asBooleanDefault(value.active, true),
      sequence: this.asNumber(value.sequence, 0),
      account_mapping: this.asNullableString(value.account_mapping),
      description: this.asNullableString(value.description),
      internal_note: this.asNullableString(value.internal_note),
      usage_count: this.asNumber(value.usage_count, 0),
      can_delete: this.asBoolean(value.can_delete),
      created_at: this.asString(value.created_at),
      updated_at: this.asString(value.updated_at),
    };
  }

  private normalizeCurrency(value: any): FinanceCurrencyRecord | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const id = this.asString(value.id);
    const code = this.normalizeCurrencyIso(value.code || value.iso_code || value.currency || value.name);
    const name = this.asString(value.name) || code;

    if (!id || !code) {
      return null;
    }

    return {
      id,
      code,
      name,
      symbol: this.asNullableString(value.symbol),
      company_base_currency: this.asBoolean(value.company_base_currency),
      active: this.asBooleanDefault(value.active, true),
      active_for_purchases: this.asBooleanDefault(value.active_for_purchases, false),
      selectable_on_documents: this.asBooleanDefault(value.selectable_on_documents, true),
    };
  }

  private clearTaxCache(): void {
    this.taxRequests.clear();
  }

  private responseTotalPages(response: any): number {
    const totalPages = this.asNumber(response?.pagination?.total_pages ?? response?.total_pages, 1);
    return Math.max(1, Math.floor(totalPages));
  }

  private normalizeTaxAmountType(value: unknown): FinanceTaxAmountType | '' {
    const normalized = this.asString(value).toLowerCase().replace(/[_\s-]+/g, '');
    if (normalized === 'percentage' || normalized === 'percent') {
      return 'percentage';
    }
    if (normalized === 'fixed' || normalized === 'amount') {
      return 'fixed';
    }
    if (
      normalized === 'division' ||
      normalized === 'percentincluded' ||
      normalized === 'percentageincluded' ||
      normalized === 'priceincluded' ||
      normalized === 'percentageofpricetaxincluded'
    ) {
      return 'division';
    }
    if (normalized === 'python' || normalized === 'pythoncode' || normalized === 'code') {
      return 'python_code';
    }
    if (normalized === 'group') {
      return 'group';
    }
    return '';
  }

  private normalizeCurrencyIso(value: unknown): string {
    const normalized = this.asString(value).trim().toUpperCase();
    return /^[A-Z]{3}$/.test(normalized) ? normalized : '';
  }

  private asString(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }

    return value.trim();
  }

  private asNullableString(value: unknown): string | null {
    const normalized = this.asString(value);
    return normalized.length > 0 ? normalized : null;
  }

  private asStringArray(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value.map((item) => this.asString(item)).filter(Boolean);
    }

    if (typeof value === 'string') {
      return value.split(',').map((item) => item.trim()).filter(Boolean);
    }

    return [];
  }

  private asBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'string') {
      return value.toLowerCase() === 'true';
    }

    if (typeof value === 'number') {
      return value !== 0;
    }

    return false;
  }

  private asBooleanDefault(value: unknown, fallback: boolean): boolean {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }

    return this.asBoolean(value);
  }

  private asNumber(value: unknown, fallback: number): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }

    return fallback;
  }

  private operationQuery(query: FinanceOperationListQuery): string {
    const params = new URLSearchParams();
    if (query.recurringOnly) params.set('recurring_only', 'true');
    if (query.originId) params.set('origin_id', query.originId);
    const search = query.search ?? query.searchPayload?.query ?? null;

    params.set('page', String(query.page ?? 1));
    params.set('perPage', String(query.perPage ?? 25));

    if (search) {
      params.set('search', search);
    }

    if (query.state) {
      params.set('state', query.state);
    }

    if (query.dateFrom) {
      params.set('date_from', query.dateFrom);
    }

    if (query.dateTo) {
      params.set('date_to', query.dateTo);
    }

    if (query.sortBy) {
      params.set('sort_by', query.sortBy);
    }

    if (query.sortDirection) {
      params.set('sort_direction', query.sortDirection);
    }

    for (const filter of query.searchPayload?.filters ?? []) {
      const value = filter.value;
      if (Array.isArray(value)) {
        params.set(filter.key, value.join(','));
      } else if (typeof value === 'object' && value !== null) {
        const range = value as { from?: string; to?: string };
        if (range.from) params.set(`${filter.key}_from`, range.from);
        if (range.to) params.set(`${filter.key}_to`, range.to);
      } else if (value !== null && value !== undefined && String(value).length > 0) {
        params.set(filter.key, String(value));
      }
    }

    if (query.searchPayload?.sort?.column) {
      params.set('sort_by', query.searchPayload.sort.column);
      params.set('sort_direction', query.searchPayload.sort.direction);
    }

    return `?${params.toString()}`;
  }

  private normalizeCustomer(value: any): FinanceCustomerRecord | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const id = this.asString(value.id);
    const accountName = this.asString(value.account_name ?? value.company ?? value.name);
    const contactName = this.asString(value.contact_name ?? value.name);

    if (!id || !accountName || !contactName) {
      return null;
    }

    return {
      id,
      account_name: accountName,
      contact_name: contactName,
      email: this.asNullableString(value.email),
      phone: this.asNullableString(value.phone),
      receivables_balance: this.asNumber(value.receivables_balance, 0),
      invoice_count: this.asNumber(value.invoice_count, 0),
      payment_count: this.asNumber(value.payment_count, 0),
      follow_up_status: this.asString(value.follow_up_status) || 'normal',
    };
  }

  private normalizePaymentTerm(value: any): FinancePaymentTermRecord | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const id = this.asString(value.id);
    const name = this.asString(value.name);

    if (!id || !name) {
      return null;
    }

    return {
      id,
      name,
      due_days: this.asNumber(value.due_days, 0),
      installment_rule: this.asNullableString(value.installment_rule),
      description: this.asNullableString(value.description),
      is_default: this.asBoolean(value.is_default),
      active: this.asBoolean(value.active),
      usage_count: this.asNumber(value.usage_count, 0),
      can_delete: this.asBoolean(value.can_delete),
      created_at: this.asString(value.created_at),
      updated_at: this.asString(value.updated_at),
    };
  }

  private normalizeFollowUpLevel(value: any): FinanceFollowUpLevelRecord | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const id = this.asString(value.id);
    const name = this.asString(value.name);

    if (!id || !name) {
      return null;
    }

    return {
      id,
      name,
      delay_days: this.asNumber(value.delay_days, 0),
      reminder_action: this.asNullableString(value.reminder_action),
      message_template: this.asNullableString(value.message_template),
      sequence: this.asNumber(value.sequence, 0),
      active: this.asBoolean(value.active),
      usage_count: this.asNumber(value.usage_count, 0),
      can_delete: this.asBoolean(value.can_delete),
      created_at: this.asString(value.created_at),
      updated_at: this.asString(value.updated_at),
    };
  }

  private normalizeIncoterm(value: any): FinanceIncotermRecord | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const id = this.asString(value.id);
    const code = this.asString(value.code);
    const name = this.asString(value.name);

    if (!id || !code || !name) {
      return null;
    }

    return {
      id,
      code,
      name,
      description: this.asNullableString(value.description),
      active: this.asBoolean(value.active),
      usage_count: this.asNumber(value.usage_count, 0),
      can_delete: this.asBoolean(value.can_delete),
      created_at: this.asString(value.created_at),
      updated_at: this.asString(value.updated_at),
    };
  }

  private normalizeBankAccount(value: any): FinanceBankAccountRecord | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const id = this.asString(value.id);
    const bankName = this.asString(value.bank_name);
    const accountName = this.asString(value.account_name);
    const accountNumber = this.asString(value.account_number);
    const currency = this.asString(value.currency);

    if (!id || !bankName || !accountName || !accountNumber || !currency) {
      return null;
    }

    return {
      id,
      bank_name: bankName,
      account_name: accountName,
      account_number: accountNumber,
      currency,
      journal_mapping: this.asNullableString(value.journal_mapping),
      active: this.asBoolean(value.active),
      usage_count: this.asNumber(value.usage_count, 0),
      can_delete: this.asBoolean(value.can_delete),
      created_at: this.asString(value.created_at),
      updated_at: this.asString(value.updated_at),
    };
  }

  private normalizeReconciliationModel(value: any): FinanceReconciliationModelRecord | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const id = this.asString(value.id);
    const label = this.asString(value.label);
    const amountMatching = this.asString(value.amount_matching);

    if (!id || !label || !amountMatching) {
      return null;
    }

    return {
      id,
      label,
      description: this.asNullableString(value.description),
      matching_rules: this.asNullableString(value.matching_rules),
      amount_matching: amountMatching,
      partner_matching: this.asNullableString(value.partner_matching),
      account_mapping: this.asNullableString(value.account_mapping),
      sequence: this.asNumber(value.sequence, 0),
      active: this.asBoolean(value.active),
      usage_count: this.asNumber(value.usage_count, 0),
      can_delete: this.asBoolean(value.can_delete),
      created_at: this.asString(value.created_at),
      updated_at: this.asString(value.updated_at),
    };
  }
}
